import * as React from 'react';
import {
    Box,
    Chip,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {useTranslation} from 'react-i18next';
import type {BranchStatusRow} from '../../hooks/useDashboard';
import BranchStatusTableMobile from './mobile/BranchStatusTableMobile';
import {evidenceChip} from './branchStatusHelpers';

interface BranchStatusTableProps {
    readonly rows: BranchStatusRow[];
    readonly isMobile: boolean;
}

export default function BranchStatusTable({rows, isMobile}: Readonly<BranchStatusTableProps>): React.ReactElement {
    const {t} = useTranslation();

    return (
        <>
            <Typography gutterBottom sx={{color: 'text.primary', fontSize: 18, fontWeight: 600, textAlign: 'left'}}>
                {t('dashboard.branchStatus.title')}
            </Typography>
            <Divider/>
            <br/>
            {rows.length === 0 ? (
                <Box sx={{py: 4, textAlign: 'center'}}>
                    <Typography variant="body2" sx={{color: 'text.secondary'}}>
                        {t('dashboard.branchStatus.empty')}
                    </Typography>
                </Box>
            ) : isMobile ? (
                <BranchStatusTableMobile rows={rows}/>
            ) : (
                <TableContainer>
                    <Table aria-label="branch status table">
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('dashboard.branchStatus.header.branch')}</TableCell>
                                <TableCell align="right">{t('dashboard.branchStatus.header.employees')}</TableCell>
                                <TableCell align="right">{t('dashboard.branchStatus.header.evidence')}</TableCell>
                                <TableCell align="right">{t('dashboard.branchStatus.header.action')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row) => (
                                <TableRow key={row.branchId} sx={{'&:last-child td': {border: 0}}}>
                                    <TableCell component="th" scope="row">
                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                            <LocationOnOutlinedIcon fontSize="small" color="action"/>
                                            <Typography sx={{fontWeight: 600}}>{row.branchName}</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell align="right">{row.employeeCount}</TableCell>
                                    <TableCell align="right">{evidenceChip(row, t)}</TableCell>
                                    <TableCell align="right">
                                        <Chip
                                            label={t('common.actions.view')}
                                            size="small"
                                            variant="outlined"
                                            color="secondary"
                                            icon={<ArrowForwardIcon/>}
                                            onClick={row.onView}
                                            sx={{cursor: 'pointer'}}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </>
    );
}
